import { useCallback } from "react";
import { getColumnIndex, getColumnLetter } from "../utils/tableHelpers";

export const useClipboardOperations = ({
  rows,
  columns,
  selectedRange,
  clipboardData,
  setRows,
  setClipboardData,
  setPendingChanges,
  setEditHistory,
  onDataUpdate,
  showNotification
}) => {
  // Copy a single cell - NO CHANGE (just copies, doesn't modify)
  const copyCell = useCallback((rowIndex, columnId) => {
    const value = rows[rowIndex][columnId];
    setClipboardData({
      type: 'single',
      value: value,
      rangeData: null,
      rangeDimensions: null,
      operation: 'copy',
      sourceRange: null
    });
    
    setEditHistory(prev => [...prev, {
      id: Date.now(),
      type: 'copy_cell',
      row: rowIndex + 1,
      column: getColumnLetter(getColumnIndex(columns, columnId)),
      value: value,
      timestamp: new Date().toISOString()
    }]);
    
    showNotification('Cell copied to clipboard', 'info');
    return value;
  }, [rows, columns, setClipboardData, setEditHistory, showNotification]);

  // Cut a single cell - ADD TO PENDING CHANGES
  const cutCell = useCallback((rowIndex, columnId) => {
    const oldValue = rows[rowIndex][columnId];
    
    const updated = [...rows];
    updated[rowIndex][columnId] = "";
    setRows(updated);
    onDataUpdate?.(updated);
    
    setClipboardData({
      type: 'single',
      value: oldValue,
      rangeData: null,
      rangeDimensions: null,
      operation: 'cut',
      sourceRange: null
    });
    
    const change = {
      id: Date.now(),
      type: 'edit_cell',
      row: rowIndex + 1,
      column: getColumnLetter(getColumnIndex(columns, columnId)),
      value: "",
      oldValue: oldValue,
      operation: 'cut',
      timestamp: new Date().toISOString()
    };
    
    setPendingChanges(prev => [...prev, change]);
    setEditHistory(prev => [...prev, change]);
    
    showNotification('Cell cut to clipboard', 'info');
    return oldValue;
  }, [rows, columns, setRows, onDataUpdate, setClipboardData, setPendingChanges, setEditHistory, showNotification]);

  // Paste to a single cell - ADD TO PENDING CHANGES
  const pasteCell = useCallback((rowIndex, columnId) => {
    if (!clipboardData?.value) {
      showNotification('Nothing to paste', 'warning');
      return;
    }
    
    const oldValue = rows[rowIndex][columnId];
    const newValue = clipboardData.value;
    
    const updated = [...rows];
    updated[rowIndex][columnId] = newValue;
    setRows(updated);
    onDataUpdate?.(updated);
    
    const change = {
      id: Date.now(),
      type: 'edit_cell',
      row: rowIndex + 1,
      column: getColumnLetter(getColumnIndex(columns, columnId)),
      value: newValue,
      oldValue: oldValue,
      operation: 'paste',
      timestamp: new Date().toISOString()
    };
    
    setPendingChanges(prev => [...prev, change]);
    setEditHistory(prev => [...prev, change]);
    
    if (clipboardData.operation === 'cut') {
      setClipboardData({
        type: null,
        value: null,
        rangeData: null,
        rangeDimensions: null,
        operation: null,
        sourceRange: null
      });
    }
    
    showNotification('Cell pasted', 'info');
  }, [rows, columns, setRows, onDataUpdate, clipboardData, setPendingChanges, setEditHistory, showNotification]);

  // Copy a range of cells - NO CHANGE (just copies)
  const copySelection = useCallback(() => {
    if (!selectedRange) {
      showNotification('No selection to copy', 'warning');
      return null;
    }
    
    const { startRow, startCol, endRow, endCol } = selectedRange;
    const rangeData = [];
    
    for (let r = startRow; r <= endRow; r++) {
      const rowData = [];
      for (let c = startCol; c <= endCol; c++) {
        const columnId = columns[c].id;
        rowData.push(rows[r][columnId]);
      }
      rangeData.push(rowData);
    }
    
    setClipboardData({
      type: 'range',
      value: null,
      rangeData: rangeData,
      rangeDimensions: {
        rows: endRow - startRow + 1,
        cols: endCol - startCol + 1
      },
      operation: 'copy',
      sourceRange: selectedRange
    });
    
    setEditHistory(prev => [...prev, {
      id: Date.now(),
      type: 'copy_range',
      range: selectedRange,
      timestamp: new Date().toISOString()
    }]);
    
    showNotification(`Copied ${rangeData.length} × ${rangeData[0]?.length || 0} cells`, 'info');
    return rangeData;
  }, [selectedRange, rows, columns, setClipboardData, setEditHistory, showNotification]);

  // Cut a range of cells - ADD TO PENDING CHANGES
  const cutSelection = useCallback(() => {
    if (!selectedRange) {
      showNotification('No selection to cut', 'warning');
      return null;
    }
    
    const { startRow, startCol, endRow, endCol } = selectedRange;
    const rangeData = [];
    const changes = [];
    const updated = [...rows];
    
    for (let r = startRow; r <= endRow; r++) {
      const rowData = [];
      for (let c = startCol; c <= endCol; c++) {
        const columnId = columns[c].id;
        const oldValue = rows[r][columnId];
        rowData.push(oldValue);
        
        updated[r][columnId] = "";
        changes.push({
          id: Date.now() + r * 100 + c,
          type: 'edit_cell',
          row: r + 1,
          column: getColumnLetter(c),
          value: "",
          oldValue: oldValue,
          operation: 'cut',
          timestamp: new Date().toISOString()
        });
      }
      rangeData.push(rowData);
    }
    
    setRows(updated);
    onDataUpdate?.(updated);
    setClipboardData({
      type: 'range',
      value: null,
      rangeData: rangeData,
      rangeDimensions: {
        rows: endRow - startRow + 1,
        cols: endCol - startCol + 1
      },
      operation: 'cut',
      sourceRange: selectedRange
    });
    
    setPendingChanges(prev => [...prev, ...changes]);
    setEditHistory(prev => [...prev, ...changes]);
    
    showNotification(`Cut ${rangeData.length} × ${rangeData[0]?.length || 0} cells`, 'info');
    return rangeData;
  }, [selectedRange, rows, columns, setRows, onDataUpdate, setClipboardData, setPendingChanges, setEditHistory, showNotification]);

  // Paste to a range starting at specific cell - ADD TO PENDING CHANGES
  const pasteToSelection = useCallback((startRow, startCol) => {
    if (!clipboardData?.rangeData) {
      showNotification('No range data to paste', 'warning');
      return;
    }
    
    const rangeData = clipboardData.rangeData;
    const changes = [];
    const updated = [...rows];
    
    for (let r = 0; r < rangeData.length; r++) {
      for (let c = 0; c < rangeData[r].length; c++) {
        const targetRow = startRow + r;
        const targetCol = startCol + c;
        
        if (targetRow >= rows.length || targetCol >= columns.length) continue;
        
        const columnId = columns[targetCol].id;
        const oldValue = rows[targetRow][columnId];
        const newValue = rangeData[r][c];
        
        updated[targetRow][columnId] = newValue;
        changes.push({
          id: Date.now() + r * 100 + c,
          type: 'edit_cell',
          row: targetRow + 1,
          column: getColumnLetter(targetCol),
          value: newValue,
          oldValue,
          operation: clipboardData.operation === 'cut' ? 'paste_after_cut' : 'paste',
          timestamp: new Date().toISOString()
        });
      }
    }
    
    setRows(updated);
    onDataUpdate?.(updated);
    setPendingChanges(prev => [...prev, ...changes]);
    setEditHistory(prev => [...prev, ...changes]);
    
    if (clipboardData.operation === 'cut') {
      setClipboardData({
        type: null,
        value: null,
        rangeData: null,
        rangeDimensions: null,
        operation: null,
        sourceRange: null
      });
    }
    
    showNotification(`Pasted ${rangeData.length} × ${rangeData[0]?.length || 0} cells`, 'info');
  }, [clipboardData, rows, columns, setRows, onDataUpdate, setPendingChanges, setEditHistory, showNotification]);

  return {
    copyCell,
    cutCell,
    pasteCell,
    copySelection,
    cutSelection,
    pasteToSelection
  };
};
